import React, { Component } from 'react';
import PropTypes from 'prop-types';
import UltimatePaginationBootstrap3 from 'react-ultimate-pagination-bootstrap-3';
import CsvDownloadButton from './CsvDownloadButton.jsx';
import MultiSelectTable from 'ar-multi-select-table';
import i18n from 'ar-i18n';
import moment from 'moment';
import _ from 'lodash';
import { transformPerformanceData, renderCurrency, renderNumber, renderPercent, renderDate } from '../utils/reporting.js';
import { getConversionDataCutoffDate } from '../utils/dates.js';
import { setReportingSort } from '../actions.js';

// Note that ar-multi-select-table requires data item have EID field.
const eidColumn = {
    key: 'eid',
    label: '',
    accessor: item => item.eid
};

class DailyPerformanceTable extends Component {
    static propTypes = {
        i18nLoaded: PropTypes.bool,
        dispatch: PropTypes.func,
        advertisableEid: PropTypes.string,
        dailyPerformance: PropTypes.array,
        strategy: PropTypes.object,
        reportingSort: PropTypes.object,
        dailyAttributions: PropTypes.object
    };

    state = {
        data: [],
        currentPage: 1,
        pageSize: 50,
        selectedItemKeys: {},
        cutoffDate: getConversionDataCutoffDate()
    };

    render() {
        const data = this.props.dailyPerformance && this.props.dailyAttributions ? this.props.dailyPerformance : [];
        const totalPages = Math.ceil(data.length / this.state.pageSize);
        const showPaginationMenu = data.length <= this.state.pageSize;
        const start = (this.state.currentPage - 1) * this.state.pageSize;
        const end = this.state.currentPage * this.state.pageSize;
        let transformedData = [];
        for (let perf of data) {
            let dailyAttributions = null;
            if (perf.date.getTime() in this.props.dailyAttributions) {
                dailyAttributions = this.props.dailyAttributions[perf.date.getTime()];
            }
            let row = transformPerformanceData(this.props.strategy, perf, dailyAttributions);
            row.exclude = moment.utc(row.date).isAfter(this.state.cutoffDate);
            transformedData.push(row);
        }
        let itemsInCurrentPage = transformedData.slice(start, end);
        const selectedDataItems = data && !_.isEmpty(this.state.selectedItemKeys) ?
            transformedData.filter(item => this.state.selectedItemKeys[item.eid]) : null;
        return (
            <div className='performance-reporting daily-performance-reporting'>
                <div>
                    <div className='download-csv-button'>
                        <CsvDownloadButton
                            columns={ this.getDailyColumns(true) }
                            // if the user does not select any row, it will export all of the rows in current page.
                            reportItems={ (!_.isEmpty(selectedDataItems)) ? selectedDataItems : transformedData }
                            reportName='daily-performance'
                        />
                    </div>
                </div>
                <div>
                    <MultiSelectTable
                        columns={ [eidColumn].concat(this.getDailyColumns()) }
                        data={ itemsInCurrentPage }
                        i18n={ i18n }
                        rowKey={ 'date' }
                        selectedData={ this.state.selectedItemKeys }
                        bulkActions={ [] }
                        rowSelection={ true }
                        onSelection={ this.onRowClick.bind(this) }
                        onSort={ this.onSort.bind(this) }
                        sort={ this.props.reportingSort }
                    />
                </div>
                <div>
                    <div className='pagination-buttons'>
                        <UltimatePaginationBootstrap3
                            className={ showPaginationMenu ? 'hidden' : 'shown' }
                            prev
                            next
                            first
                            last
                            ellipsis
                            boundaryLinks
                            totalPages={ totalPages }
                            maxButtons={ 3 }
                            currentPage={ totalPages === 0 ? 0 : this.state.currentPage }
                            onChange={ this.handlePageSelect.bind(this) }
                        />
                    </div>
                </div>
            </div>
        );
    }

    valueOrDash = (item, value) => {
        // Determines if we should display a dash or the value provided,
        // depending on the 'exclude' property of the item. Some
        // items were marked with exclude = True because their dates
        // were AFTER this.state.cutoffDate.
        return item.exclude ? '—' : value;
    };

    getDailyColumns(forCsv = false) {
        return [{
            key: 'date',
            label: i18n.gettext('Date'),
            accessor: item => item.date,
            render: x => renderDate(x)
        }, {
            key: 'spend',
            label: i18n.gettext('Spend'),
            accessor: item => item.spend,
            render: b => renderCurrency(b, forCsv)
        }, {
            key: 'clicks',
            label: i18n.gettext('Clicks'),
            accessor: item => item.clicks,
            render: x => renderNumber(x, forCsv)
        }, {
            key: 'ecpc',
            label: i18n.gettext('eCPC'),
            accessor: item => item.ecpc,
            render: b => renderCurrency(b, forCsv)
        }, {
            key: 'cv',
            label: i18n.gettext('CV'),
            accessor: item => item,
            render: item => this.valueOrDash(item, renderNumber(item.conversions, forCsv))
        }, {
            key: 'cvr',
            label: i18n.gettext('CVR'),
            accessor: item => item,
            render: item => this.valueOrDash(item, renderPercent(item.cvr))
        }, {
            key: 'gms',
            label: i18n.gettext('GMS'),
            accessor: item => item,
            render: item => this.valueOrDash(item, renderCurrency(item.gms, forCsv)),
            widthMultiplier: 2
        }, {
            key: 'cpa',
            label: i18n.gettext('CPA'),
            accessor: item => item,
            render: item => this.valueOrDash(item, renderCurrency(item.cpa, forCsv)),
            widthMultiplier: 2
        }, {
            key: 'roas',
            label: i18n.gettext('ROAS'),
            accessor: item => item,
            render: item => this.valueOrDash(item, renderPercent(item.roas))
        }, {
            key: 'aov',
            label: i18n.gettext('AOV'),
            accessor: item => item,
            render: item => this.valueOrDash(item, renderCurrency(item.aov, forCsv))
        }];
    }

    onRowClick(selectedElements) {
        this.setState({
            selectedItemKeys: _.merge({}, this.state.selectedItemKeys, selectedElements)
        });
    }

    onSort(key, direction) {
        if (key === '' && direction === '') {
            this.props.dispatch(setReportingSort({accessor: null, direction: '', key: ''}));
            return;
        }
        let column = _.find(this.getDailyColumns(), c => c.key === key);
        this.props.dispatch(setReportingSort({accessor: column.accessor, direction: direction, key: key}));
    }

    handlePageSelect(page) {
        this.setState({
            currentPage: page
        });
    }

}

export default DailyPerformanceTable;
